import React from 'react';
import type { FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  FormikWrapper,
  FormEmail as EmailField,
} from '@uncinc/uncinc-react-kitchen-sink';

import { Button, PasswordField } from '../../../../components';
import useOperationStatus from '../../../../hooks/useOperationStatus';
import useToast from '../../../../hooks/useToast';
import useTypedDispatch from '../../../../hooks/useTypedDispatch';
import { getRegistrationData } from '../../../../store/actions/Registrations';
import { userUpdate } from '../../../../store/actions/User';

interface FormValues {
  password: string;
  new_email: string;
  new_email_confirm: string;
}

const AccountChangeEmailForm: React.FC = () => {
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const toast = useToast();

  const changeEmailOperationStatus = useOperationStatus('user', 'update');
  const currentUserId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));

  const validationSchema = Yup.object().shape({
    new_email: Yup
      .string()
      .email(t('pages.account.change_email.fields.new_email.validation.invalid'))
      .required(t('pages.account.change_email.fields.new_email.validation.required')),
    new_email_confirm: Yup
      .string()
      .oneOf([Yup.ref('new_email'), null], t('pages.account.change_email.fields.new_email_confirm.validation.match'))
      .required(t('pages.account.change_email.fields.new_email_confirm.validation.required')),
    password: Yup
      .string()
      .required(t('pages.account.change_email.fields.password.validation.required')),
  });

  const onSubmit = (values: FormValues) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { password, new_email } = values;

    dispatch(userUpdate(currentUserId, {
      mail: [{
        value: new_email,
      }],
      name: {
        value: new_email,
      },
      pass: [{
        existing: password,
      }],
    })).then(() => {
      dispatch(getRegistrationData()).catch(() => {});
      toast.show(t('pages.account.change_email.success_message'), 'success');
    }).catch((error) => {
      toast.show(t('pages.account.change_email.error_message'), 'error');
      console.error(error);
    });
  };

  return (
    <FormikWrapper
      initialValues={{ password: '', new_email: '', new_email_confirm: '' }}
      validationSchema={validationSchema}
      onSubmit={(values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <EmailField
        fieldName="new_email"
        title={t('pages.account.change_email.fields.new_email.label')}
        placeholder={t('pages.account.change_email.fields.new_email.placeholder')}
      />
      <EmailField
        fieldName="new_email_confirm"
        title={t('pages.account.change_email.fields.new_email_confirm.label')}
        placeholder={t('pages.account.change_email.fields.new_email_confirm.placeholder')}
      />
      <PasswordField
        fieldName="password"
        title={t('pages.account.change_email.fields.password.label')}
      />
      <Button
        htmlType="submit"
        buttonState={changeEmailOperationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
      >
        {t('buttons.save')}
      </Button>
    </FormikWrapper>
  );
};

export default AccountChangeEmailForm;
